import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe, Lock, UserCheck, FileText, Download, Trash2, Edit, Eye, ArrowLeft, CheckCircle, Shield } from 'lucide-react';
import LegalRequestModal from '../components/LegalRequestModal';

type RequestType = 'access' | 'rectification' | 'erasure' | 'restriction' | 'portability' | 'objection';

export default function GDPR() {
  const lastUpdated = "November 16, 2025";
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    type: RequestType | null;
    title: string;
    description: string;
  }>({
    isOpen: false,
    type: null,
    title: '',
    description: '',
  });

  const openModal = (type: RequestType, title: string, description: string) => {
    setModalState({
      isOpen: true,
      type,
      title,
      description,
    });
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      type: null,
      title: '',
      description: '',
    });
  };

  return (
    <article className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-blue-100 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center mb-6">
            <Globe className="h-12 w-12 mr-4" />
            <h1 className="text-4xl md:text-5xl font-bold">GDPR Compliance</h1>
          </div>
          <p className="text-xl text-blue-100">
            Last updated: {lastUpdated}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-8 rounded-r-lg">
          <p className="text-gray-700 leading-relaxed">
            CyberEdge is committed to protecting your personal data and respecting your privacy rights under the General Data Protection Regulation (GDPR). This page explains how we comply with GDPR requirements and your rights under this regulation.
          </p>
        </div>

        <section className="mb-12">
          <div className="flex items-center mb-4">
            <Shield className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">What is GDPR?</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              The General Data Protection Regulation (GDPR) is a comprehensive data protection law that came into effect on May 25, 2018. It applies to all companies processing the personal data of individuals residing in the European Union (EU) and European Economic Area (EEA), regardless of the company's location.
            </p>
            <p className="mb-4">
              GDPR gives individuals more control over their personal data and places strict obligations on organizations that process such data. CyberEdge is fully committed to GDPR compliance and has implemented appropriate measures to protect your data.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Legal Basis for Processing</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              Under GDPR, we must have a legal basis to process your personal data. We rely on the following legal bases:
            </p>
            <div className="space-y-4">
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Consent</h3>
                <p className="text-gray-700">
                  You have given clear consent for us to process your personal data for specific purposes, such as marketing communications or newsletter subscriptions.
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Contract</h3>
                <p className="text-gray-700">
                  Processing is necessary for a contract we have with you, or because you have asked us to take specific steps before entering into a contract.
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Legal Obligation</h3>
                <p className="text-gray-700">
                  Processing is necessary for us to comply with the law (not including contractual obligations).
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Legitimate Interests</h3>
                <p className="text-gray-700">
                  Processing is necessary for our legitimate interests or the legitimate interests of a third party, unless there is a good reason to protect your personal data which overrides those legitimate interests.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <div className="flex items-center mb-4">
            <UserCheck className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">Your Rights Under GDPR</h2>
          </div>
          <div className="space-y-4">
            <div className="bg-white border-l-4 border-blue-500 p-6 shadow-sm rounded-r-lg">
              <div className="flex items-start">
                <Eye className="h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Right to Access</h3>
                  <p className="text-gray-700 mb-3">
                    You have the right to request copies of your personal data. We may charge a small fee for this service.
                  </p>
                  <button
                    onClick={() => openModal('access', 'Request Data Access', 'Request a copy of all personal data we hold about you.')}
                    className="inline-flex items-center px-4 py-2 border border-blue-600 text-sm font-medium rounded-lg text-blue-600 bg-white hover:bg-blue-50 transition-colors"
                  >
                    Request Data Access
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white border-l-4 border-green-500 p-6 shadow-sm rounded-r-lg">
              <div className="flex items-start">
                <Edit className="h-6 w-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Right to Rectification</h3>
                  <p className="text-gray-700 mb-3">
                    You have the right to request that we correct any information you believe is inaccurate or complete information you believe is incomplete.
                  </p>
                  <button
                    onClick={() => openModal('rectification', 'Request Data Correction', 'Request correction of inaccurate or incomplete personal data.')}
                    className="inline-flex items-center px-4 py-2 border border-green-600 text-sm font-medium rounded-lg text-green-600 bg-white hover:bg-green-50 transition-colors"
                  >
                    Request Data Correction
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white border-l-4 border-red-500 p-6 shadow-sm rounded-r-lg">
              <div className="flex items-start">
                <Trash2 className="h-6 w-6 text-red-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Right to Erasure</h3>
                  <p className="text-gray-700 mb-3">
                    You have the right to request that we erase your personal data, under certain conditions.
                  </p>
                  <button
                    onClick={() => openModal('erasure', 'Request Data Deletion', 'Request deletion of your personal data under GDPR right to erasure.')}
                    className="inline-flex items-center px-4 py-2 border border-red-600 text-sm font-medium rounded-lg text-red-600 bg-white hover:bg-red-50 transition-colors"
                  >
                    Request Data Deletion
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white border-l-4 border-orange-500 p-6 shadow-sm rounded-r-lg">
              <div className="flex items-start">
                <Lock className="h-6 w-6 text-orange-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Right to Restrict Processing</h3>
                  <p className="text-gray-700 mb-3">
                    You have the right to request that we restrict the processing of your personal data, under certain conditions.
                  </p>
                  <button
                    onClick={() => openModal('restriction', 'Request Processing Restriction', 'Request restriction on processing of your personal data.')}
                    className="inline-flex items-center px-4 py-2 border border-orange-600 text-sm font-medium rounded-lg text-orange-600 bg-white hover:bg-orange-50 transition-colors"
                  >
                    Request Processing Restriction
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white border-l-4 border-purple-500 p-6 shadow-sm rounded-r-lg">
              <div className="flex items-start">
                <Download className="h-6 w-6 text-purple-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Right to Data Portability</h3>
                  <p className="text-gray-700 mb-3">
                    You have the right to request that we transfer the data we have collected to another organization, or directly to you, under certain conditions.
                  </p>
                  <button
                    onClick={() => openModal('portability', 'Request Data Export', 'Request a copy of your data in a portable format.')}
                    className="inline-flex items-center px-4 py-2 border border-purple-600 text-sm font-medium rounded-lg text-purple-600 bg-white hover:bg-purple-50 transition-colors"
                  >
                    Request Data Export
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white border-l-4 border-gray-500 p-6 shadow-sm rounded-r-lg">
              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-gray-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Right to Object</h3>
                  <p className="text-gray-700 mb-3">
                    You have the right to object to our processing of your personal data, under certain conditions.
                  </p>
                  <button
                    onClick={() => openModal('objection', 'Object to Processing', 'Object to the processing of your personal data.')}
                    className="inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-lg text-gray-600 bg-white hover:bg-gray-50 transition-colors"
                  >
                    Object to Processing
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <div className="flex items-center mb-4">
            <FileText className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">How We Protect Your Data</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              We have implemented appropriate technical and organizational measures to ensure a level of security appropriate to the risk, including:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Pseudonymization and encryption of personal data</li>
              <li>Regular testing, assessment, and evaluation of security measures</li>
              <li>Ability to ensure ongoing confidentiality, integrity, and availability of systems</li>
              <li>Ability to restore availability and access to data in case of incidents</li>
              <li>Staff training on data protection and GDPR compliance</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Secure data backup and disaster recovery procedures</li>
            </ul>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Processing Agreements</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              When we use third-party service providers to process personal data on our behalf, we ensure they:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Only process data according to our documented instructions</li>
              <li>Implement appropriate technical and organizational security measures</li>
              <li>Ensure confidentiality of personnel processing the data</li>
              <li>Assist us in responding to data subject requests</li>
              <li>Delete or return all personal data after the end of services</li>
              <li>Make available all information necessary to demonstrate compliance</li>
            </ul>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">International Data Transfers</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              When transferring personal data outside the EU/EEA, we ensure adequate protection through:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>EU Commission adequacy decisions</li>
              <li>Standard Contractual Clauses approved by the EU Commission</li>
              <li>Binding Corporate Rules</li>
              <li>Privacy Shield certification (where applicable)</li>
              <li>Explicit consent from data subjects</li>
            </ul>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Breach Notification</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              In the event of a personal data breach, we will:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Notify the relevant supervisory authority within 72 hours of becoming aware of the breach (unless the breach is unlikely to result in a risk to rights and freedoms)</li>
              <li>Notify affected individuals without undue delay if the breach is likely to result in a high risk to their rights and freedoms</li>
              <li>Document all data breaches, including facts, effects, and remedial action taken</li>
              <li>Implement measures to address the breach and prevent future breaches</li>
            </ul>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Protection Officer</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              We have appointed a Data Protection Officer (DPO) who is responsible for overseeing our data protection strategy and GDPR compliance. You can contact our DPO regarding:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Questions about how we process your personal data</li>
              <li>Exercising your rights under GDPR</li>
              <li>Concerns about our data protection practices</li>
              <li>Data protection impact assessments</li>
            </ul>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-6">
              <p className="mb-2"><strong>Data Protection Officer</strong></p>
              <p className="mb-2">Email: dpo@cyberedgesolutions.com</p>
              <p className="mb-2">Phone: +91 6301644590</p>
              <p>Address: PJR Arcade, Kukatpally, Hyderabad, Telangana 500085</p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Filing a Complaint</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              If you believe we have not complied with GDPR requirements, you have the right to lodge a complaint with your local supervisory authority. However, we would appreciate the opportunity to address your concerns before you approach a supervisory authority, so please contact us first.
            </p>
            <p className="mb-4">
              You can find your local supervisory authority by visiting the European Data Protection Board website at <a href="https://edpb.europa.eu/" className="text-blue-600 hover:text-blue-800">edpb.europa.eu</a>.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Exercise Your Rights</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              To exercise any of your rights under GDPR, please:
            </p>
            <ol className="list-decimal pl-6 mb-4 space-y-2">
              <li>Submit a request by email to info@cyberedgesolutions.com</li>
              <li>Clearly state which right you wish to exercise</li>
              <li>Provide sufficient information to verify your identity</li>
              <li>Include any relevant details about your request</li>
            </ol>
            <p className="mb-4">
              We will respond to your request within one month. In complex cases, we may extend this period by two additional months. We will not charge a fee for processing your request unless it is manifestly unfounded or excessive.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              If you have questions about GDPR compliance or wish to exercise your rights, please contact us:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <p className="mb-2"><strong>CyberEdge Solutions - GDPR Compliance Team</strong></p>
              <p className="mb-2">Email: info@cyberedgesolutions.com</p>
              <p className="mb-2">Phone: +91 6301644590</p>
              <p>Address: PJR Arcade, Kukatpally, Hyderabad, Telangana 500085</p>
            </div>
          </div>
        </section>

        <div className="flex flex-wrap gap-4 pt-8 border-t border-gray-200">
          <Link
            to="/privacy"
            className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            Privacy Policy
          </Link>
          <Link
            to="/terms"
            className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            Terms of Service
          </Link>
          <Link
            to="/cookies"
            className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            Cookie Policy
          </Link>
        </div>
      </div>

      {modalState.isOpen && modalState.type && (
        <LegalRequestModal
          isOpen={modalState.isOpen}
          onClose={closeModal}
          requestType={modalState.type}
          title={modalState.title}
          description={modalState.description}
        />
      )}
    </article>
  );
}
